// Added by Sejune Oh on 2019-03-11
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, observable, computed, makeObservable } from 'mobx'
import axios, { AxiosError } from 'axios'

import {
  reqCreateCleanupRule,
  reqGetCleanupRule,
  reqUpdateCleanupRule,
} from '@/service/dataStorage/cleanupConditions.service'

import { CleanupRule } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Util
import { getDiskSizeFromMib } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root store
import Rootstore from '@/stores'

// Store Base
import MgmtStoreBase from '@/stores/base/MgmtStoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import { DSConfigIncomingVolumeElemenProps } from '@/stores/dataStorage/config/dsConfigIncomingVolumeDiskStore'
import { DEFAULT_DRIVERS } from '@/helper/define'
import Logger from '@/helper/Logger'
import Intl from '@/helper/Intl'

interface DataStorageBaseElementProps {
  id?: string
  fk_data_node_id?: string
  fk_import_node_id?: string
  fk_group_id?: string
  data_token?: string
  import_token?: string
  name?: string
  description?: string
  db_driver_name?: string
  db_connection_string?: string
  scalable?: boolean
  root_dir_path?: string
  root_dir_credential?: string
  local_path_alias?: string
  created_at?: string
  updated_at?: string
  deleted_at?: string
}

export interface DataStorageElementProps extends DataStorageBaseElementProps {
  [key: string]: any
  data_connnected?: string
  import_connnected?: string
  storage_rule?: string
  disk_usage?: string
  is_advanced?: boolean
  name_with_path?: string
  group_key?: string
  attached_at?: string
  detached_at?: string
  driver_name?: string
  connection_string?: string
  original_root_dir_path?: string
  original_name?: string
  original_db_driver_name?: string
  is_existed_storage?: boolean
  original_db_cnt_str?: string
  file_receiving_enabled?: boolean
  stor_backup_enabled?: boolean
}

export interface AssignedDataStorageElementProps {
  id: string
  name: string
  description: string
  data_token: string
  import_token: string
  connected: boolean
  fk_group_id: string
  group_key: string
}

interface ResCheckStoragePath {
  driver_name?: string
  connection_string?: string
  data_token?: string
  import_token?: string
  created_at?: string
  attached_at?: string
  detached_at?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DataStorageStore extends MgmtStoreBase<DataStorageElementProps> {
  totalCount: number = 0
  elements: Array<DataStorageElementProps> = []
  cachedStorage?: DataStorageElementProps

  root?: Rootstore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: Rootstore) {
    super({ storeName: 'DataStorageStore', apiUrl: 'mgmt/datastorages', _root: root })
    this.root = root
    makeObservable(this, {
      totalCount: observable,
      elements: observable,
      cachedStorage: observable,
      setTotalCount: action,
      setElements: action,
      setStorageListWithGroupAndDiskUsage: action,
      cacheStore: action,
      clearCachedStore: action,
      hasStorage: computed,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElement = async (id: string) => {
    return await super.getSingle(id)
  }

  getCleanupRule = async (token: string) => {
    return await reqGetCleanupRule(token)
  }

  getElements = async (path: string, countCheck = false) => {
    const rtnData = await super.getAll(path)
    if (countCheck) await this.setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<DataStorageElementProps>
  }

  addElement = async (
    element: DataStorageElementProps,
    funcBeforeUpdateTotalCount?: (arg: any) => Promise<any>,
    requiredProps?: Array<string>
  ) => {
    const newElement = await super.add(element)
    if (funcBeforeUpdateTotalCount) {
      let args: { [key: string]: any } = {}
      for (let key of requiredProps!) {
        args[key] = newElement[key]
      }
      await funcBeforeUpdateTotalCount(args)
    }
    this.setTotalCount(this.totalCount + 1)
    return [newElement.id, newElement.data_token]
  }

  addCleanupRule = async (token: string, rule: CleanupRule) => {
    return reqCreateCleanupRule(token, rule)
  }

  removeElement = async (id: string) => {
    const rtn = await super.remove(id)
    this.setTotalCount(this.totalCount - 1)
    return rtn
  }

  updateElement = async (id: string, element: DataStorageElementProps) => {
    const updatedElement = await super.updateSingle(id, element)
    return updatedElement.id
  }

  updateCleanupRule = async (token: string, rule: CleanupRule) => {
    return reqUpdateCleanupRule(token, rule)
  }

  addDefaultStorage = async (element: DataStorageElementProps) => {
    const newElement = await super.add(element)
    return [newElement.id, newElement.data_token]
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  checkDuplicatedDataStorageName = async (dataStorageName: string) => {
    return super.checkDuplicatedByField({ field: 'name', value: dataStorageName })
  }

  checkDuplicatedDataStoragePath = async (dataStoragePath: string) => {
    const getDataStorageQuery = this.getQueryPath({})
    const dataStorageList = await this.getElements(getDataStorageQuery)
    if (dataStorageList.length === 0) return false

    const targetPath = dataStoragePath.toLowerCase()
    const duplicated = dataStorageList.find(({ root_dir_path }) => root_dir_path!.toLowerCase() === targetPath)
    return !!duplicated
  }

  checkPath = async (storagePath: string) => {
    return await axios.get<ResCheckStoragePath>(`/v1/mgmt/datastorages-check/storage-path?dir_path=${storagePath}`)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setElements = (elements: Array<DataStorageElementProps>) => {
    this.elements = elements
  }

  setStorageListWithGroupAndDiskUsage = async (
    elements: Array<DataStorageElementProps>,
    getGroupKey: (key: string) => string | undefined,
    diskProxyTokenSetter: (key: string) => void,
    diskUsageGetter: () => Promise<{
      err: AxiosError | Error | undefined
      element?: DSConfigIncomingVolumeElemenProps
    }>
  ) => {
    const elementsWithDetail = []

    for (let i = 0, max = elements.length; i < max; i++) {
      const dataStorage = elements[i]
      const dataToken = dataStorage.data_token as string
      let diskUsage: string = '-'
      let groupKey: string = '-'

      // Set Data Storage Proxy Token before use api
      try {
        diskProxyTokenSetter(dataToken)
        const { err, element } = await diskUsageGetter()

        if (err) {
          diskUsage = Intl.getIntlText({ id: 'htmljs_react.global.datastoragestore.msg_invalid_path' })
          Logger.errLog(`Error from get diskUsage / dataToken: ${dataToken}`)
          // @ts-ignore
          console.log(err.response ? err.response.data.message : err.message || err)
        } else {
          const { disk_total_size_mib: total, disk_used_size_mib: used } = element!
          diskUsage = `${getDiskSizeFromMib(used)} / ${getDiskSizeFromMib(total)}`
        }
      } catch (err) {
        Logger.errLog(`Error from getting diskUsage / uncaught`)
        // @ts-ignore
        console.log(err.response ? err.response.data.message : err.message || err)
      }

      // Get group key
      try {
        groupKey = getGroupKey(dataStorage.fk_group_id!) || '-'
      } catch (err) {
        Logger.errLog(`Error from getting group key / uncaught`)
        // @ts-ignore
        console.log(err.response ? err.response.data.message : err.message || err)
      }

      const driver = DEFAULT_DRIVERS[dataStorage.db_driver_name! === DEFAULT_DRIVERS[0].id ? 0 : 1]
      elementsWithDetail.push({
        ...dataStorage,
        db_driver_name: driver.label,
        disk_usage: diskUsage,
        group_key: groupKey,
      })
    }

    this.elements = elementsWithDetail as Array<DataStorageElementProps>
  }

  cacheStore = (storage: DataStorageElementProps) => {
    this.cachedStorage = storage
  }

  clearCachedStore = () => {
    this.cachedStorage = undefined
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get hasStorage() {
    return this.totalCount > 0
  }
}
